import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../icons';
import ZipLocation from '../FormComponent/zipLocation';
import DialogueBox from '../../DialogueBox';
import { useCountries } from '@/containers/CreationForms/AddEditWorkkard/hooks/useCountries';
import { FALLBACK_COUNTRIES } from '@/components/FieldEngine';

// Moved out of the component — pure function of its argument, no need to
// live inside the component or be recreated every render.
const removeStripTags = (str) => {
    if (typeof str !== "string") return undefined;
    const openingTag = str.match(/<[A-Za-z0-9='"\s+]+>/g);
    const closingTag = str.match(/<\/[A-Za-z0-9='"\s+]+>/g);
    if (openingTag && closingTag) {
        return str
            .replace(/<[A-Za-z0-9='"\s+]+>/g, "")
            .replace(/<\/[A-Za-z0-9='"\s+]+>/g, "")
            .trim();
    }
    return str.trim();
};

const ServiceLocation = (props) => {
    const { countries, loading } = useCountries("property");
    const countriesOptions = countries.length > 0 ? countries : FALLBACK_COUNTRIES;

    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [tags, setTags] = useState([]);
    const [waterMark, setWaterMark] = useState("");
    const [countriesData, setCountriesData] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState("");
    const [locationNames, setLocationNames] = useState([]);
    const [isRecordFound, setIsRecordFound] = useState(true);
    const [validationError, setValidationError] = useState({ isErrror: false, errorMsg: "" });
    const [isModal, setIsModal] = useState(false);
    const [zipId, setZipId] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);

    const menuRef = useRef(null);
    

    useEffect(() => {
        if (countriesOptions && !loading) {
            const selectedCountryData = countriesOptions[0];
            setCountriesData(countriesOptions);
            setSelectedCountry(selectedCountryData.value);
            setWaterMark(selectedCountryData?.water_mark);
        }
    }, [countriesOptions]);

    const closeDropDown = () => {
        setIsOpen(false);
        setSearchText('');
        setLocationNames([]);
        setIsRecordFound(true);
        setSelectedItem(null);
    };

    useEffect(() => {
        const onClickOutsideSelect = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                closeDropDown();
            }
        };
        document.addEventListener('mousedown', onClickOutsideSelect);
        return () => document.removeEventListener('mousedown', onClickOutsideSelect);
    }, []);

    const prevLocationNamesRef = useRef(props.locationNames);
    useEffect(() => {
        const prevLocationNames = prevLocationNamesRef.current;
        prevLocationNamesRef.current = props.locationNames;
        if (prevLocationNames === props.locationNames) return;

        setLocationNames(props.locationNames);
        setIsRecordFound(props.locationNames.length > 0);
    }, [props.locationNames]);

    useEffect(()=>{
        if(props.serviceLocData) {
            const newLocData = props.serviceLocData.map((item)=>{
                return {
                    id: item.Id,
                    value: item.LocationKey
                }   
            }) ;
            setTags(newLocData);
        }
    }, [props.serviceLocData]);    

    const onSearchText = (e) => {
        setSearchText(e.target.value);
        props.fetchServiceLocData({ queryParam: e.target.value, countryCode: selectedCountry });
    };

    const validate = (existingTags, selectedTagId) => {
        const isExist = existingTags.some((item) => item.id == selectedTagId);
        if (isExist) {
            setValidationError({ isErrror: true, errorMsg: "Already Exist" });
            setLocationNames([]);
            return false;
        }
        return true;
    };

    const onSelected = (item) => {
        if (!validate(tags, item.id)) return;

        const updatedTags = [...tags, { id: item.id, value: removeStripTags(item.value) }];
        setTags(updatedTags);

        const updatedData = updatedTags.map((tag) => ({ location_key: tag.value }));
        props.addServiceLocationKey(updatedData);
        closeDropDown();
    };

    const removeLocationName = (key) => {
        const filterData = tags.filter((item) => item.id != key);
        const updatedData = filterData.map((item) => ({ location_key: item.value }));
        setTags(filterData);
        props.removeServiceLocNames(updatedData);
    };

    const handleCountryChange = (key) => {
        const selectedCountryData = countriesData.find((item) => item.value === key);
        setWaterMark(selectedCountryData.water_mark);
        setSelectedCountry(key);
    };

    const addZipLocation = (data) => {
        data.forEach((item) => onSelected(item));
    };

    const handleKeyDown = (e) => {
        if (e.keyCode === 40 || e.keyCode === 38) {
            const selector = "serviceLocations";
            const listSize = locationNames.length;
            const list = locationNames;
            if (listSize === 0) return;

            let newSelected = selectedItem == null ? 0 : selectedItem;
            if (e.keyCode === 38) {
                // Up
                newSelected = selectedItem === 0 ? 0 : selectedItem - 1;
            } else if (e.keyCode === 40) {
                // Down
                newSelected = selectedItem === listSize - 1 ? listSize - 1 : (selectedItem + 1) % listSize;
            }
            setSelectedItem(newSelected);

            const id = list[newSelected].id;
            if (newSelected === listSize - 1) {
                // ending
                return;
            }
            if (newSelected === 0) {
                document.getElementById(selector).scrollTo(0, 0); // beginning
                return;
            }

            const scrollTop = document.getElementById(selector).scrollTop;
            const selectedElementTop = document.getElementById(id).offsetTop;
            const selectedElementHeight = document.getElementById(id).offsetHeight;
            const scrollWindowHeight = document.getElementById(selector).offsetHeight;

            // no scroll if selected element top position and bottom position is inside the scroll window
            if (
                selectedElementTop > scrollTop + 80 &&
                selectedElementTop + selectedElementHeight < scrollTop + scrollWindowHeight
            ) {
                return;
            }
            if (e.keyCode === 38) {
                document.getElementById(selector).scrollBy(0, -selectedElementHeight);
            } else if (e.keyCode === 40) {
                document.getElementById(selector).scrollBy(0, selectedElementHeight);
            }
        } else if (e.keyCode === 13 && selectedItem != null) {
            onSelected(locationNames[selectedItem]);
        }
    };

    const toggleHover = (index) => {
        setSelectedItem(index);
    };

    return (
        <>
            <DialogueBox
                overlayClassName="modal"
                contentClassName="modalContent medium animated faster fadeInUpx2"
                shouldCloseOnOverlayClick={false}
                isOpen={isModal}
            >
                <ZipLocation
                    zipData={props.zipLocData}
                    fetchZipData={() => props.fetchZipData({ zipId, countryCode: selectedCountry })}
                    onCancelClick={() => setIsModal(false)}
                    addZipLocation={(data) => {
                        addZipLocation(data);
                        setIsModal(false);
                    }}
                />
            </DialogueBox>

            <div className="colFlex formTags">
                <div className="alignMiddle">
                    <div className="formGroup mrNone colFlex mr">
                        <div className="mailDropdown serviceLocation select fullWidth">
                            <div className="alignMiddle rowSpaceBetween flexGrow" onClick={() => setIsOpen(true)}>
                                <span className="gray">{waterMark}</span>
                                <span className="zip"><Icon name="zip" className="icon" /></span>
                            </div>
                            {isOpen && (
                                <div ref={menuRef} className="mailingSearch serviceLocation  zipTop onClick ">
                                    <div className={`sticky ${validationError.isErrror ? 'errorState' : ''}`}>
                                        <input
                                            type="text"
                                            placeholder={waterMark}
                                            autoFocus={true}
                                            onFocus={() => setValidationError({ isErrror: false, errorMsg: "" })}
                                            onKeyDown={handleKeyDown}
                                            onChange={onSearchText}
                                            value={searchText}
                                            maxLength="200"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                        />
                                        <span className="zip"><Icon name="zip" className="icon" /></span>
                                    </div>
                                    {locationNames.length > 0 && (
                                        <div className="select2-results serviceLocation__dropdown" id="serviceLocations">
                                            {locationNames.map((item, index) => (
                                                <div
                                                    key={item.id}
                                                    className={`serviceLocation__list ${selectedItem === index ? 'activeItem' : ''}`}
                                                    id={item.id}
                                                    onMouseEnter={() => toggleHover(index)}
                                                    onMouseLeave={() => toggleHover(index)}
                                                >
                                                    <div className="drop-down-block rowSpaceBetween alignMiddle">
                                                        <button className="btn-cc__transparent" onClick={() => onSelected(item)}>
                                                            {removeStripTags(item.value)}
                                                        </button>
                                                        {item.id && (
                                                            <span className="alignMiddle">
                                                                {' '}
                                                                <span className="mr__1">Choose by{' '}</span>
                                                                <button
                                                                    className="btn-cc__transparent btn-cc__link__blue"
                                                                    onClick={() => {
                                                                        setZipId(item.id);
                                                                        setIsModal(true);
                                                                    }}
                                                                >
                                                                    zip
                                                                </button>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {searchText.length > 0 && !isRecordFound && (
                                        <span style={{ paddingLeft: "12px" }}>No matching record found.</span>
                                    )}
                                    {validationError.isErrror && (
                                        <span style={{ paddingLeft: "12px" }} className="errorMsg">
                                            {validationError.errorMsg}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <select className="wth-4 ml" value={selectedCountry} onChange={(e) => handleCountryChange(e.target.value)}>
                        {countriesData.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {tags.length > 0 && (
                    <div className="ReactTags__selected wrap mt ">
                        {tags.map((item) => (
                            <span key={item.id} className="tag-wrapper ReactTags__tag">
                                {item.value}
                                <span className="ReactTags__remove" onClick={() => removeLocationName(item.id)}>
                                    <Icon name="close" className="icon" />
                                </span>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default ServiceLocation;